import { Injectable } from '@angular/core';
import {
    UserControllerService,
    User,
    UserControllerFind200Response,
    UsersFilter,
    PersonalRequisition,
    PersonalRequisitionsControllerService,
    PersonalRequisitionFilter,
    PersonalRequisitionType,
    PersonalRequisitionTypesControllerService,
    RequisitionCommentsControllerService,
    RequisitionCommentTypesControllerService,
    PersonalRequisitionReportControllerService,
    RequisitionStatusControllerService,
} from '@api-swagger-client/index';
import moment from 'moment';
import { Observable, tap, map, catchError, throwError } from 'rxjs';
export const personalRequisitionFilter: any | PersonalRequisitionFilter =
    JSON.stringify({
        fields: {
            modifiedOn: true,
            createdOn: true,
            createdBy: true,
            id: true,
            code: true,
            humanCapitalCompanyId: true,
            city: true,
            creationReason: true,
            requisitionTypeId: true,
            jobPositionId: true,
            currentStatusId: true,
            immediateSuperiorId: true,
            areaManagerId: true,
            generalManagerId: true,
            financeDirectorId: true,
            companyId: true,
            employeeReplaceName:true,
            employeeReplaceId:true,
            // El form de edición parte de esta fila: sin el campo, el check se abría marcado
            followsAuthorizationFlow: true,
        },
        include: [
            {
                relation: 'requisitionType',
                scope: {
                    fields: ['id', 'name', 'code'],
                },
            },
            {
                // Necesaria para saber si la requisición pasa por la dirección
                // de finanzas: el paso sólo aplica a una empresa.
                relation: 'company',
                scope: {
                    fields: ['id', 'name', 'code'],
                },
            },
            {
                relation: 'jobPosition',
                scope: {
                    fields: [
                        'id',
                        'name',
                        'positionObjective',
                        'responsibilities',
                        'leadershipPosition',
                    ],
                },
            },
            {
                relation: 'currentStatus',
                scope: {
                    fields: ['id', 'name', 'code'],
                },
            },
            {
                relation: 'createdByUser',
                scope: {
                    fields: [
                        'id',
                        'firstName',
                        'middleName',
                        'lastName',
                        'photoProfileUrl',
                        'companyId',
                    ],
                    include: [
                        {
                            relation: 'company',
                            scope: {
                                fields: ['id', 'name', 'color'],
                            },
                        },
                    ],
                },
            },
            {
                relation: 'immediateSuperior',
                scope: {
                    fields: [
                        'id',
                        'firstName',
                        'middleName',
                        'lastName',
                        'photoProfileUrl',
                    ],
                },
            },
            {
                relation: 'areaManager',
                scope: {
                    fields: [
                        'id',
                        'firstName',
                        'middleName',
                        'lastName',
                        'photoProfileUrl',
                    ],
                },
            },
            {
                relation: 'generalManager',
                scope: {
                    fields: [
                        'id',
                        'firstName',
                        'middleName',
                        'lastName',
                        'photoProfileUrl',
                    ],
                },
            },
            {
                relation: 'financeDirector',
                scope: {
                    fields: [
                        'id',
                        'firstName',
                        'middleName',
                        'lastName',
                        'photoProfileUrl',
                    ],
                },
            },
            {
                relation: 'requisitionComments',
                scope: {
                    fields: [
                        'id',
                        'requisitionId',
                        'commentTypeId',
                        'comment',
                        'userId',
                        'createdOn',
                    ],
                    order: ['createdOn DESC'],
                    include: [
                        {
                            relation: 'commentType',
                            scope: {
                                fields: ['id', 'name'],
                            },
                        },
                        {
                            relation: 'user',
                            scope: {
                                fields: [
                                    'id',
                                    'firstName',
                                    'middleName',
                                    'lastName',
                                    'photoProfileUrl',
                                ],
                            },
                        },
                    ],
                },
            },
            {
                relation: 'requisitionStatusHistories',
                scope: {
                    fields: [
                        'id',
                        'requisitionId',
                        'statusId',
                        'previousStatusId',
                        'comment',
                        'changedBy',
                        'createdOn',
                    ],
                    order: ['createdOn DESC'],
                    include: [
                        {
                            relation: 'status',
                            scope: {
                                fields: ['id', 'name', 'code'],
                            },
                        },

                        {
                            relation: 'previousStatus',
                            scope: {
                                fields: ['id', 'name'],
                            },
                        },
                        {
                            relation: 'changedUser',
                            scope: {
                                fields: [
                                    'id',
                                    'firstName',
                                    'middleName',
                                    'lastName',
                                    'photoProfileUrl',
                                ],
                            },
                        },
                    ],
                },
            },
        ],
    });
@Injectable({
    providedIn: 'root',
})
export class PersonalRequisitionService {
    constructor(
        private personalRequisitionsControllerService: PersonalRequisitionsControllerService,
        private personalRequisitionTypesControllerService: PersonalRequisitionTypesControllerService,
        private requisitionCommentsControllerService: RequisitionCommentsControllerService,
        private requisitionCommentTypesControllerService: RequisitionCommentTypesControllerService,
        private personalRequisitionReportControllerService: PersonalRequisitionReportControllerService,
        private requisitionStatusControllerService: RequisitionStatusControllerService
    ) {}
    getAllRequisitions = ({}: any): Observable<PersonalRequisition[]> => {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerFind(personalRequisitionFilter)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(error);
                })
            );
    };
    getRequisitionById = (
        requisitionId: string
    ): Observable<PersonalRequisition> => {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerFindById(
                requisitionId,
                personalRequisitionFilter
            )
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(error);
                })
            );
    };

    mapUserData = (personalRequisition: PersonalRequisition) => ({
        ...personalRequisition,
        createdOn: moment(personalRequisition.createdOn).fromNow(),
        modifiedOn: moment(personalRequisition.modifiedOn).fromNow(),
    });

    // Método a añadir en personal-requisition.service.ts
    getRequisitionTypes(): Observable<PersonalRequisitionType[]> {
        return this.personalRequisitionTypesControllerService
            .personalRequisitionTypesControllerFind({})
            .pipe(
                catchError((error) => {
                    console.error('Error fetching requisition types:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * @param companyId Empresa de la requisición: Capital Humano sale de ella
     * y no de la empresa del usuario.
     */
    getApprovalChain(userId: number, companyId?: string): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerGetApprovalChain(userId, companyId)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Crea una nueva requisición de personal
     */
    createRequisition(requisition: any): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerCreate(requisition)
            .pipe(
                catchError((error) => {
                    console.error('Error creating requisition:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Actualiza una requisición existente
     */
    updateRequisition(id: string, requisition: any): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerUpdateById(id, requisition)
            .pipe(
                catchError((error) => {
                    console.error('Error updating requisition:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Elimina una requisición
     */
    deleteRequisition(id: string): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerDeleteById(id)
            .pipe(
                catchError((error) => {
                    console.error('Error deleting requisition:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Crea un comentario para la requisición
     */
    createRequisitionComment(comment: any): Observable<any> {
        return this.requisitionCommentsControllerService
            .requisitionCommentsControllerCreate(comment)
            .pipe(
                catchError((error) => {
                    console.error('Error creating comment:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Obtiene los tipos de comentarios
     */
    getCommentTypes(): Observable<any[]> {
        return this.requisitionCommentTypesControllerService
            .requisitionCommentTypesControllerFind({})
            .pipe(
                catchError((error) => {
                    console.error('Error fetching comment types:', error);
                    return throwError(error);
                })
            );
    }

    getAvailableActions = (
        requisitionId: string
    ): Observable<PersonalRequisition> => {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerGetAvailableActions(requisitionId)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(error);
                })
            );
    };

    /**
     * Manda a aprobación requisición de personal
     */
    submitForApproval(
        requisitionId: string,
        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerSubmitForApproval(
                requisitionId,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error submitting requisition:', error);
                    return throwError(error);
                })
            );
    }

    approveRequisition(
        requisitionId: string,
        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerApproveRequisition(
                requisitionId,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error approving requisition:', error);
                    return throwError(error);
                })
            );
    }

    rejectRequisition(
        requisitionId: string,
        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerRejectRequisition(
                requisitionId,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error rejecting requisition:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Obtiene una reducción de personal por ID desde el link publico
     */
    findByIdFromLink(id: string, tokenLink: string): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerFindByIdFromLink(
                id,
                tokenLink
            )
            .pipe(
                tap((response: any) => {
                    if (!response.success && response.statusCode !== 200) {
                        throw new Error(
                            response.message || 'Error al obtener la reducción'
                        );
                    }
                }),
                catchError((error) => {
                    console.error(
                        `Error fetching personal reduction with id ${id}:`,
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    approveRequisitionPublic(
        requisitionId: string,
        token: string,

        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerApproveRequisitionPublic(
                requisitionId,
                token,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error approving requisition:', error);
                    return throwError(error);
                })
            );
    }

    rejectRequisitionPublic(
        requisitionId: string,
        token: string,
        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalRequisitionsControllerService
            .personalRequisitionsControllerRejectRequisitionPublic(
                requisitionId,
                token,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error rejecting requisition:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Obtiene los catálogos para filtros de reportes de personal requisition
     */
    getStatus(): Observable<any> {
        return this.requisitionStatusControllerService
            .requisitionStatusControllerFind()

            .pipe(
                catchError((error) => {
                    console.error('Error fetching report catalogs:', error);
                    return throwError(error);
                })
            );
    }
    /**
     * Obtiene los catálogos para filtros de reportes de personal requisition
     */
    getReportCatalogs(): Observable<any> {
        return this.personalRequisitionReportControllerService
            .personalRequisitionReportControllerGetCatalogs()
            .pipe(
                catchError((error) => {
                    console.error('Error fetching report catalogs:', error);
                    return throwError(error);
                })
            );
    }

    /**
     * Obtiene datos de requisiciones de personal para reportes con filtros aplicados
     */
    getPersonalRequisitionsReport(
        filter?: any,
        includeDeleted = false,
        search?: string
    ): Observable<any> {
        const filterParam: { [key: string]: any } = {};

        if (filter) {
            if (filter.where) {
                filterParam['where'] = filter.where;
            }
            if (filter.limit) {
                filterParam['limit'] = filter.limit;
            }
            if (filter.skip) {
                filterParam['skip'] = filter.skip;
            }
            if (filter.order && filter.order.length > 0) {
                filterParam['order'] = filter.order;
            }
        }

        // Convertir el filtro a JSON string
        const filterString: any =
            Object.keys(filterParam).length > 0
                ? JSON.stringify(filterParam)
                : undefined;

        return this.personalRequisitionReportControllerService
            .personalRequisitionReportControllerGetVacanciesReport(
                includeDeleted,
                filterString,
                search
            )
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error fetching personal requisitions report:',
                        error
                    );
                    return throwError(error);
                })
            );
    }

    /**
     * Construye filtros predeterminados para requisiciones de personal
     */
    getDefaultPersonalRequisitionFilters(): any[] {
        return [
            {
                where: { currentStatus: { code: 'DRAFT' } },
                limit: 100,
            },
            {
                where: { currentStatus: { code: 'PENDING' } },
                limit: 100,
            },
            {
                where: { currentStatus: { code: 'APPROVED' } },
                limit: 100,
            },
            {
                where: {
                    createdOn: {
                        gte: new Date(
                            Date.now() - 30 * 24 * 60 * 60 * 1000
                        ).toISOString(),
                    },
                },
                limit: 100,
            },
        ];
    }

    /**
     * Construye un filtro dinámico basado en criterios para personal requisition
     */
    buildDynamicPersonalRequisitionFilter(criteria: any[]): any {
        const where: any = {};

        criteria.forEach((criterion) => {
            if (
                criterion.value !== null &&
                criterion.value !== undefined &&
                criterion.value !== ''
            ) {
                switch (criterion.operator) {
                    case 'eq':
                        where[criterion.field] = { eq: criterion.value };
                        break;
                    case 'neq':
                        where[criterion.field] = { neq: criterion.value };
                        break;
                    case 'like':
                        where[criterion.field] = {
                            like: `%${criterion.value}%`,
                        };
                        break;
                    case 'gt':
                        where[criterion.field] = { gt: criterion.value };
                        break;
                    case 'gte':
                        where[criterion.field] = { gte: criterion.value };
                        break;
                    case 'lt':
                        where[criterion.field] = { lt: criterion.value };
                        break;
                    case 'lte':
                        where[criterion.field] = { lte: criterion.value };
                        break;
                    case 'in':
                        where[criterion.field] = {
                            inq: Array.isArray(criterion.value)
                                ? criterion.value
                                : [criterion.value],
                        };
                        break;
                    case 'nin':
                        where[criterion.field] = {
                            nin: Array.isArray(criterion.value)
                                ? criterion.value
                                : [criterion.value],
                        };
                        break;
                }
            }
        });

        return { where, limit: 100 };
    }
     getPersonalRequisitionReportExcel(
            filter?: any,
            includeDeleted = false,
            search?: string
        ): Observable<Blob> {
            const filterParam: { [key: string]: any } = {};

            if (filter) {
                if (filter.where) {
                    filterParam['where'] = filter.where;
                }
                if (filter.skip) {
                    filterParam['skip'] = filter.skip;
                }
                if (filter.order && filter.order.length > 0) {
                    filterParam['order'] = filter.order;
                }
            }

            const filterString: PersonalRequisitionFilter | any =
                Object.keys(filterParam).length > 0
                    ? JSON.stringify(filterParam)
                    : undefined;

            return this.personalRequisitionReportControllerService.personalRequisitionReportControllerExportPersonalRequisitionsToExcel (
                includeDeleted,
                filterString,
                search
            );
        }
    /**
     * Genera PDF de requisición de personal
     * @param requisitionId - ID de la requisición
     * @returns Observable con el blob del PDF
     */
    generatePdf(requisitionId: string): Observable<Blob> {
        return this.personalRequisitionReportControllerService
            .personalRequisitionReportControllerGeneratePdf(requisitionId)
            .pipe(
                catchError((error) => {
                    console.error('Error generating PDF:', error);
                    return throwError(error);
                })
            );
    }
}
